/**
 * Upcoming Schedule Widget
 * Horizontal carousel of upcoming lessons with "Book new" CTA.
 * Moved from StudentDashboardBeta (Phase 3.2 extraction).
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { BffNextLesson, BffScheduleDay } from '../../types/bff-v2'

export interface UpcomingScheduleWidgetProps {
  nextLesson: BffNextLesson | null
  schedule: BffScheduleDay[]
  className?: string
}

function getDayOfMonth(): number {
  return new Date().getDate()
}

function getMonthAbbr(): string {
  return new Date().toLocaleString('en-US', { month: 'short' }).toUpperCase()
}

const UpcomingScheduleWidget: React.FC<UpcomingScheduleWidgetProps> = ({
  nextLesson,
  schedule,
  className = '',
}) => {
  const navigate = useNavigate()
  const today = getDayOfMonth()
  const monthAbbr = getMonthAbbr()

  const allItems = schedule.flatMap(day =>
    day.items.map(item => ({ ...item, date: day.date, dayLabel: day.day }))
  )

  return (
    <div className={className}>
      <div className="flex gap-[10px] overflow-x-auto scroll-snap-x-mandatory pb-[4px] scrollbar-hide">
        {/* Today's lesson / next lesson */}
        {nextLesson ? (
          <div
            className="flex-shrink-0 w-[clamp(155px,22vw,185px)] scroll-snap-align-start
                       bg-white border border-[rgba(91,164,181,.35)]
                       rounded-[var(--radius-r)] p-[15px] cursor-pointer
                       transition-all duration-[.18s] relative overflow-hidden
                       hover:-translate-y-[2px] hover:shadow-[var(--shadow-sh2)]"
            onClick={() => navigate('/dashboard/lessons')}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: 'linear-gradient(90deg, var(--color-cyan), var(--color-neon))' }}
            />
            <div className="text-[9px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[.6px] mb-[8px]">
              Today · {monthAbbr} {today}
            </div>
            <div className="text-[28px] font-black leading-none" style={{ color: 'var(--color-cyan)' }}>
              {today}
            </div>
            <div className="text-[13px] font-bold text-[var(--color-text-primary)] mt-[6px]">
              {nextLesson.title}
            </div>
            <div className="text-[11px] text-[var(--color-text-secondary)] mt-[2px]">
              {nextLesson.teacher_name}
            </div>
            <div className="text-[10px] text-[var(--color-text-tertiary)] mt-[1px]">
              {nextLesson.datetime} · {nextLesson.duration_min} min
            </div>
          </div>
        ) : allItems.length > 0 ? (
          <div
            className="flex-shrink-0 w-[clamp(155px,22vw,185px)] scroll-snap-align-start
                       bg-white border border-[rgba(91,164,181,.35)]
                       rounded-[var(--radius-r)] p-[15px] cursor-pointer
                       transition-all duration-[.18s] relative overflow-hidden
                       hover:-translate-y-[2px] hover:shadow-[var(--shadow-sh2)]"
            onClick={() => navigate('/dashboard/lessons')}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: 'linear-gradient(90deg, var(--color-cyan), var(--color-neon))' }}
            />
            <div className="text-[9px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[.6px] mb-[8px]">
              Next
            </div>
            <div className="text-[13px] font-bold text-[var(--color-text-primary)] mt-[6px]">
              {allItems[0].title}
            </div>
            <div className="text-[11px] text-[var(--color-text-secondary)] mt-[2px]">
              {allItems[0].teacher}
            </div>
            <div className="text-[10px] text-[var(--color-text-tertiary)] mt-[1px]">
              {allItems[0].time}
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 w-[clamp(155px,22vw,185px)] scroll-snap-align-start
                          bg-[var(--color-card2)] border border-[var(--color-border-light)]
                          rounded-[var(--radius-r)] p-[15px] flex items-center justify-center">
            <span className="text-[13px] text-[var(--color-text-tertiary)]">No upcoming lessons</span>
          </div>
        )}

        {/* Remaining upcoming items */}
        {allItems.slice(nextLesson ? 0 : 1).slice(0, 3).map((item, i) => (
          <div
            key={item.id}
            className="flex-shrink-0 w-[clamp(155px,22vw,185px)] scroll-snap-align-start
                       bg-[var(--color-card2)] border border-[var(--color-border-light)]
                       rounded-[var(--radius-r)] p-[15px] cursor-pointer
                       transition-all duration-[.18s] relative overflow-hidden
                       hover:-translate-y-[2px] hover:shadow-[var(--shadow-sh2)]"
            onClick={() => navigate('/dashboard/lessons')}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--color-g200)]" />
            <div className="text-[9px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[.6px] mb-[8px]">
              {item.dayLabel}
            </div>
            <div className="text-[13px] font-bold text-[var(--color-text-primary)] mt-[6px]">
              {item.title}
            </div>
            <div className="text-[11px] text-[var(--color-text-secondary)] mt-[2px]">
              {item.teacher}
            </div>
            <div className="text-[10px] text-[var(--color-text-tertiary)] mt-[1px]">
              {item.time}
            </div>
          </div>
        ))}

        {/* Book new CTA */}
        <div
          className="flex-shrink-0 w-[clamp(155px,22vw,185px)] scroll-snap-align-start
                     bg-[var(--color-card2)] border border-[var(--color-border-light)]
                     rounded-[var(--radius-r)] p-[15px] cursor-pointer
                     transition-all duration-[.18s] relative overflow-hidden
                     hover:-translate-y-[2px] hover:shadow-[var(--shadow-sh2)]"
          onClick={() => navigate('/dashboard/lessons')}
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--color-g200)]" />
          <div className="text-[9px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[.6px] mb-[8px]">
            Book new
          </div>
          <div className="text-[32px] font-black leading-none" style={{ color: 'var(--color-teal)' }}>+</div>
          <div className="text-[13px] font-bold text-[var(--color-text-primary)] mt-[6px]">Book Lesson</div>
          <div className="text-[11px] text-[var(--color-text-secondary)] mt-[2px]">Check availability</div>
        </div>
      </div>
    </div>
  )
}

export default UpcomingScheduleWidget
